<template>
  <div class="ma-4">
    <v-snackbar v-model="msg" top>
      {{ text }}
      <v-btn color="pink" text @click="msg = false">Close</v-btn>
    </v-snackbar>
    <ClientNav class="hideOnPrint" />
    <v-row dense>
      <v-col cols="12" lg="3" xl="2">
        <ClientSide />
      </v-col>
      <v-col cols="12" lg="9" xl="10">
        <v-card class="pa-2" outlined tile height="100%">
          <DataTable
            :headers="headers"
            :items="medChanges"
            :server-items-length="pagination.total"
            :loading="isLoading"
            :search="searchKeyword"
            :items-per-page="itemsPerPage"
            :isAuditorRole="isAuditorRole"
            title="Medication Changes"
            @fetch-data="debounceFetchData"
            @update:options="handlePagination"
            @update:search="handleSearch"
            @on-view-item="onViewItem"
            @on-delete-item="onDeleteItem"
          >
            <template v-slot:headerAction>
              <action-control-menu
                ref="actions"
                :actions="['Print', 'Download']"
                :navigateTo="navigateTo"
                :payload="payload"
                :isAuditorRole="isAuditorRole"
                btnTitle="New"
              />
            </template>
          </DataTable>
        </v-card>
      </v-col>
    </v-row>
    <Foot />

    <OptionMenu :onAction="handleActions" :showActions="showActions" />
  </div>
</template>

<script>
import axios from "axios";
import ClientNav from "@/components/ClientNav";
import ClientSide from "@/components/ClientSide";
import Foot from "@/components/Footer";
import DateTimeUtil from "../../../utils/date-time.util";
import DataTable from "@/components/DataTable";
import ActionControlMenu from "@/components/ActionControlMenu";
import { mapActions, mapGetters } from "vuex";

const url = require("../../client_config/config").url;

export default {
  components: {
    ClientNav,
    ClientSide,
    Foot,
    DataTable,
    ActionControlMenu,
  },
  data() {
    return {
      headers: [
        {
          text: "Date of Medication Change",
          align: "start",
          sortable: true,
          value: "Date",
        },
        { text: "Medication", value: "Medication" },
        { text: "Staff", value: "Staff" },
        { text: "Signed", value: "Signature" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      msg: false,
      text: "",
      medChanges: [],
      clientID: this.$route.params.clientID,
      isLoading: true,
      searchKeyword: "",
      itemsPerPage: 10,
      currentPage: 1,
      pagination: {
        total: 0,
        pages: 0,
      },
      showActions: {
        add: true,
        print: true,
        download: true,
      },
      navigateTo: {},
      payload: {},
    };
  },
  created() {
    this.debounceFetchData();
    this.navigateTo = {
      path: `/medChange/${this.clientID}/add`,
    };
  },

  computed: {
    ...mapGetters({
      client: "clients/clientData",
    }),
    isAuditorRole() {
      return localStorage.getItem("userRole") === 'Auditor'
    }
  },

  mounted(){
    this.showActions.add = !this.isAuditorRole;
  },

  methods: {
    ...mapActions({
      generateDynamicReport: "reports/generateDynamicReport",
    }),

    initializePayload() {
      this.payload = {
        query: {
          name: this.client?.Name,
          clientId: this.client?._id,
          generateFile: true,
          page: this.currentPage,
          limit: this.itemsPerPage,
          keyword: this.searchKeyword,
          searchFields: this.headers
            .filter((h) => h.value !== "actions")
            .map((h) => h.value),
        },
        endpoints: `medChange/reports/items`,
        dynamic: true,
        fileName: `${this.client?.Name}-medication-change.pdf`,
      };
    },

    debounceFetchData() {
      this.fetchItem();
      this.initializePayload()
    },

    onViewItem(item) {
      this.$router.push({
        path: `/medChange/${this.clientID}/edit`,
        query: { id: item._id, new: false },
      });
    },

    async fetchItem() {
      this.isLoading = true;
      try {
        const response = await axios.get(`${url}/medChange/reports/items`, {
          params: {
            clientId: this.clientID,
            page: this.currentPage,
            limit: this.itemsPerPage,
            keyword: this.searchKeyword,
            searchFields: this.headers
              .filter((h) => h.value !== "actions")
              .map((h) => h.value),
          },
        });
        this.medChanges = response.data.results.map((item) => ({
          ...item,
          Date: DateTimeUtil.toLocalDate(item.Date),
          SignatureDate: DateTimeUtil.toLocalDate(item.SignatureDate),
        }));
        this.pagination = response.data.pagination;
      } catch (error) {
        console.error("Error fetching medication changes:", error);
      } finally {
        this.isLoading = false;
      }
    },

    onDeleteItem(item) {
      if (confirm("Are you sure you want to delete this medication change?")) {
        this.deleteItem(item._id);
      }
    },

    async deleteItem(id) {
      try {
        await axios.delete(`${url}/medChange/${id}`);
        this.debounceFetchData();
        this.msg = true;
        this.text = "The medication change item has been deleted successfully.";
      } catch (error) {
        console.error("Error deleting medication change:", error);
      }
    },
    
    handlePagination(options) {
      this.currentPage = options.page;
      this.itemsPerPage = options.itemsPerPage;
      this.debounceFetchData();
    },

    handleSearch(newKeyword) {
      this.searchKeyword = newKeyword;
      this.debounceFetchData();
    },

    handleActions(action) {
      if (this.$refs.actions) {
        this.$refs.actions.onSelectAction(action);
      } else {
        console.error("reference not found");
      }
    },
  },

  watch: {
    client: {
      immediate: true,
      handler(client) {
        if (client) {
          this.initializePayload();
        }
      },
    },
    isAuditorRole(newVal) {
      this.showActions.add = !newVal; 
    }
  },
};
</script>
